import React, { Component, useState } from 'react';
import './LinkSection.scss';

class EmbedErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error) {
        console.error('Link embed failed:', error);
    }

    render() {
        if (this.state.hasError) return null;
        return this.props.children;
    }
}

const flattenContentfulValue = (value) => {
    try {
        if (value == null) return '';
        if (typeof value === 'string') return value;
        if (typeof value === 'number' || typeof value === 'boolean') return String(value);
        if (Array.isArray(value)) return value.map(flattenContentfulValue).join('');
        if (typeof value === 'object') {
            if (typeof value.value === 'string') return value.value;
            if (Array.isArray(value.content)) return value.content.map(flattenContentfulValue).join('');
        }
        return '';
    } catch (error) {
        console.error('Could not read Contentful embed value:', error);
        return '';
    }
};

const getBandcampParam = (src, name, fallback) => {
    const match = src.match(new RegExp(`(?:^|/)${name}=([^/]+)`, 'i'));
    return match ? match[1] : fallback;
};

const toPublicBandcampSrc = (src) => {
    const albumId = getBandcampParam(src, 'album');
    const trackId = getBandcampParam(src, 'track');
    if (!albumId && !trackId) return src;

    const parts = [];
    if (albumId) parts.push(`album=${albumId}`);
    else parts.push(`track=${trackId}`);

    parts.push(`size=${getBandcampParam(src, 'size', 'large')}`);
    parts.push(`bgcol=${getBandcampParam(src, 'bgcol', 'ffffff')}`);
    parts.push(`linkcol=${getBandcampParam(src, 'linkcol', '0687f5')}`);

    if (/\/tracklist=true(?:\/|$)/i.test(src)) parts.push('tracklist=true');
    const artwork = getBandcampParam(src, 'artwork');
    if (artwork) parts.push(`artwork=${artwork}`);
    parts.push('transparent=true');

    return `https://bandcamp.com/EmbeddedPlayer/${parts.join('/')}/`;
};

const parseBandcampIframe = (raw) => {
    try {
        const html = flattenContentfulValue(raw)
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&amp;/g, '&');

        if (!html) return null;

        const srcMatch = html.match(/src=["']([^"']+)["']/i)
            || html.match(/(https:\/\/bandcamp\.com\/EmbeddedPlayer\/[^\s"'<>]+)/i);

        const rawSrc = srcMatch?.[1]?.trim().replace(/\s+/g, '');
        if (!rawSrc || !/^https:\/\/bandcamp\.com\/EmbeddedPlayer\//i.test(rawSrc)) return null;

        const heightMatch = html.match(/height:\s*(\d+)px/i) || html.match(/\bheight=["'](\d+)/i);
        const parsedHeight = heightMatch ? Number(heightMatch[1]) : 470;
        const height = Number.isFinite(parsedHeight) && parsedHeight > 0 ? parsedHeight : 470;

        return { src: toPublicBandcampSrc(rawSrc), height };
    } catch (error) {
        console.error('Could not parse Bandcamp iframe:', error);
        return null;
    }
};

const BandcampEmbed = ({ title, src, height }) => {
    const [interactive, setInteractive] = useState(false);

    return (
        <div
            className={`bandcamp-embed-wrap${interactive ? ' is-interactive' : ''}`}
            onClick={() => setInteractive(true)}
            onMouseLeave={() => setInteractive(false)}
        >
            <iframe
                className="bandcamp-embed"
                title={title}
                src={src}
                height={height}
                style={{ border: 0, width: '100%', height }}
                seamless
                referrerPolicy="origin"
                allow="autoplay *; encrypted-media *; fullscreen *"
            />
        </div>
    );
};

const LinkSectionInner = ({ title, url, description, type, bandcampIframe }) => {
    const embedType = typeof type === 'string' ? type.trim().toLowerCase() : '';
    const safeTitle = typeof title === 'string' ? title : 'Bandcamp player';
    const safeUrl = typeof url === 'string' ? url : '';
    const safeDescription = typeof description === 'string' ? description : '';

    const getEmbedUrl = (linkUrl, linkType) => {
        if (!linkUrl) return '';

        try {
            switch (linkType) {
                case 'youtube': {
                    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
                    const match = linkUrl.match(regExp);
                    return match && match[2].length === 11
                        ? `https://www.youtube.com/embed/${match[2]}`
                        : linkUrl;
                }
                case 'spotify':
                    return linkUrl.replace('open.spotify.com', 'open.spotify.com/embed');
                case 'soundcloud':
                    return `https://w.soundcloud.com/player/?url=${linkUrl}&show_artwork=true`;
                default:
                    return linkUrl;
            }
        } catch (error) {
            console.error('Could not build embed URL:', error);
            return '';
        }
    };

    const renderEmbed = () => {
        try {
            switch (embedType) {
                case 'youtube': {
                    const embedUrl = getEmbedUrl(safeUrl, embedType);
                    if (!embedUrl) return null;
                    return (
                        <iframe
                            title={safeTitle}
                            width="560"
                            height="315"
                            src={embedUrl}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    );
                }
                case 'spotify': {
                    const embedUrl = getEmbedUrl(safeUrl, embedType);
                    if (!embedUrl) return null;
                    return (
                        <iframe
                            src={embedUrl}
                            title={safeTitle}
                            frameBorder="0"
                            allow="encrypted-media"
                            width="100%"
                            height="352"
                        />
                    );
                }
                case 'bandcamp': {
                    const embed = parseBandcampIframe(bandcampIframe);
                    if (!embed) return null;
                    return (
                        <BandcampEmbed
                            title={safeTitle}
                            src={embed.src}
                            height={embed.height}
                        />
                    );
                }
                case 'soundcloud': {
                    if (!safeUrl) return null;
                    const encodedUrl = encodeURIComponent(safeUrl);
                    const embedSrc = `https://w.soundcloud.com/player/?url=${encodedUrl}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`;
                    return (
                        <>
                            <iframe
                                width="100%"
                                height="300"
                                scrolling="no"
                                frameBorder="no"
                                allow="autoplay"
                                src={embedSrc}
                                title={safeTitle}
                            />
                            <div style={{
                                fontSize: '10px',
                                color: '#cccccc',
                                lineBreak: 'anywhere',
                                wordBreak: 'normal',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                fontFamily: 'Interstate,Lucida Grande,Lucida Sans Unicode,Lucida Sans,Garuda,Verdana,Tahoma,sans-serif',
                                fontWeight: 100
                            }}>
                                <a href="https://soundcloud.com/nthngnthng" title="nthng" target="_blank" rel="noopener noreferrer" style={{ color: '#cccccc', textDecoration: 'none' }}>nthng</a> · <a href="https://soundcloud.com/nthngnthng/if-this-world-were-mine" title="if this world were mine" target="_blank" rel="noopener noreferrer" style={{ color: '#cccccc', textDecoration: 'none' }}>if this world were mine</a>
                            </div>
                        </>
                    );
                }
                default:
                    if (!safeUrl) return null;
                    return <a href={safeUrl} target="_blank" rel="noopener noreferrer">{safeDescription || safeUrl}</a>;
            }
        } catch (error) {
            console.error('Link embed failed:', error);
            return null;
        }
    };

    return (
        <div className="embedded-link">
            {renderEmbed()}
            {safeDescription && <p>{safeDescription}</p>}
        </div>
    );
};

const LinkSection = (props) => (
    <EmbedErrorBoundary>
        <LinkSectionInner {...props} />
    </EmbedErrorBoundary>
);

export default LinkSection;
